import { MaskitoOptions } from '@maskito/core';

/**
 * Utility functions for currency formatting
 */

/**
 * Formats amount in cents to European currency format
 * @param amountInCents Amount in cents (e.g., 250 for 2,50€)
 * @returns Formatted string (e.g., "2,50")
 */
export function formatCurrency(amountInCents: number | undefined): string {
  if (amountInCents === undefined || amountInCents === null || isNaN(amountInCents)) {
    return '0,00';
  }

  const euros = amountInCents / 100;
  return euros.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Formats amount in cents to European currency format with € symbol
 * @param amountInCents Amount in cents (e.g., 250 for 2,50€)
 * @returns Formatted string with € symbol (e.g., "2,50 €")
 */
export function formatCurrencyWithSymbol(amountInCents: number | undefined): string {
  return `${formatCurrency(amountInCents)} €`;
}

/**
 * Input mask for a euro amount typed by hand: up to six digits, then a comma (or
 * point) and at most two decimals. Anything longer is refused while typing, so
 * "9,9999" can't be entered in the first place.
 */
export const EURO_AMOUNT_MASK: MaskitoOptions = {
  mask: /^\d{0,6}(?:[.,]\d{0,2})?$/,
};

/** "12,5" or "12.50" -> 1250. Null when the text isn't an amount with ≤ 2 decimals. */
export function parseEuroToCents(value: string | number | null | undefined): number | null {
  const text = String(value ?? '').trim();
  const match = /^(\d+)(?:[.,](\d{0,2}))?$/.exec(text);
  if (!match) return null;
  return Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
}

/**
 * Formats a fee rate as a German percentage (e.g. 0.04 → "4 %", 0.035 → "3,5 %")
 * @param rate Share of the price (0.05 for 5 %)
 */
export function formatRate(rate: number): string {
  return `${(rate * 100).toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;
}
